import React, { createContext, useState, useEffect, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import supabase from '../supabaseClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [session, setSession] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const getSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) {
        console.error('Error fetching session:', error);
        setUser(null);
        setSession(null);
        setIsAdmin(false);
        return;
      }

      if (session?.user && session?.access_token) {
        const decodedToken = jwtDecode(session.access_token);
        const userRole = decodedToken.user_role || 'user';
        const isAdminUser = userRole === 'admin';
        
        setUser({ ...session.user, user_role: userRole });
        setSession(session);
        setIsAdmin(isAdminUser);
      } else {
        setUser(null);
        setSession(null);
        setIsAdmin(false);
      }
    };

    getSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user && session?.access_token) {
        const decodedToken = jwtDecode(session.access_token);
        const userRole = decodedToken.user_role || 'user';
        const isAdminUser = userRole === 'admin';
        
        setUser({ ...session.user, user_role: userRole });
        setSession(session);
        setIsAdmin(isAdminUser);
      } else {
        setUser(null);
        setSession(null);
        setIsAdmin(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
  };

  const value = {
    user,
    session,
    isAdmin,
    isLoading: user === undefined,
    isAuthenticated: !!user,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
